import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Badge, Button, Dialog, EmptyState, Field, Input, Section, Select, Spinner, type Tone } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { api, newIdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type {
  ApiKey,
  CreatedApiKey,
  CreatedWebhook,
  Delivery,
  EmailRouting,
  Integration,
  IntegrationLog,
  TestIntegrationResult,
  VerifyIntegrationsResult,
  Webhook,
} from '../lib/types';
import { useMeta } from './team';

const statusTone: Record<string, Tone> = {
  NOT_CONFIGURED: 'slate',
  SANDBOX: 'amber',
  LIVE: 'moss',
  ERROR: 'rose',
  DISABLED: 'neutral',
};

/** Bağlantı testi iletisi; ayrıntılar (ör. HTTP durumu, yönlendirme adresi) metne yerleştirilir. */
function testMessage(t: ReturnType<typeof useT>, r: { message: string; details?: unknown }): string {
  const params: Record<string, string | number> = {};
  const details = r.details && typeof r.details === 'object' ? (r.details as Record<string, unknown>) : {};
  for (const [k, v] of Object.entries(details))
    if (typeof v === 'string' || typeof v === 'number') params[k] = v;
  return t(`integrationMessages.${r.message}`, params);
}

function SecretReveal({ value, label }: { value: string; label: string }) {
  const t = useT();
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{label}</p>
      <div className="flex items-center gap-2">
        <code className="tabular block flex-1 break-all rounded-sm border border-line bg-surface-2 px-3 py-2 text-sm">
          {value}
        </code>
        <Button
          size="icon"
          aria-label={t('common.copy')}
          onClick={async () => {
            await navigator.clipboard.writeText(value);
            toast.success(t('common.copied'));
          }}
        >
          <Copy className="h-4 w-4" aria-hidden />
        </Button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ Entegrasyonlar

function IntegrationDialog({
  item,
  open,
  onOpenChange,
}: {
  item: Integration;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const [environment, setEnv] = useState<'sandbox' | 'live'>(item.environments[0] as 'sandbox' | 'live');
  const [values, setValues] = useState<Record<string, string | boolean>>(() => {
    const cfg = item.connections.find((c) => c.environment === environment)?.config ?? {};
    return Object.fromEntries(
      item.fields.map((f) => [f.key, f.type === 'boolean' ? Boolean(cfg[f.key]) : String(cfg[f.key] ?? '')]),
    );
  });
  const conn = item.connections.find((c) => c.environment === environment);
  const logs = useQuery({
    queryKey: ['integration-logs', item.key],
    queryFn: () => api.get<IntegrationLog[]>(`/v1/admin/integrations/${item.key}/logs`),
  });
  const inv = [['integrations'], ['integration-logs', item.key], ['setup'], ['system-status']];
  const save = useApiMutation(
    () => {
      const typed = Object.fromEntries(
        item.fields.map((f) => [
          f.key,
          f.type === 'number'
            ? Number(values[f.key])
            : f.type === 'boolean'
              ? Boolean(values[f.key])
              : String(values[f.key] ?? ''),
        ]),
      );
      return api.put(`/v1/admin/integrations/${item.key}/credentials`, { environment, values: typed });
    },
    { invalidate: inv, success: t('admin.integrations.saved'), silentFieldErrors: true },
  );
  const test = useApiMutation(
    () => api.post<TestIntegrationResult>(`/v1/admin/integrations/${item.key}/test`, { environment }),
    {
      invalidate: inv,
      onSuccess: (r) => {
        if (!r.result.ok) {
          toast.error(`${t('admin.integrations.testFail')}: ${testMessage(t, r.result)}`);
          return;
        }
        // Sağlayıcının başarı iletisi çevrilmişse (ör. Geliver: gönderici adresi sayısı, test ortamı) eklenir.
        const detail = testMessage(t, r.result);
        toast.success(
          detail.startsWith('integrationMessages.')
            ? t('admin.integrations.testOk')
            : `${t('admin.integrations.testOk')}: ${detail}`,
        );
      },
    },
  );
  const disconnect = useApiMutation(
    () => api.del(`/v1/admin/integrations/${item.key}?environment=${environment}`),
    { invalidate: inv, onSuccess: () => onOpenChange(false) },
  );
  const manage = can('org.integration.manage');

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      variant="sheet"
      title={t(`integrations.${item.key}.name`)}
      description={t(`integrations.${item.key}.description`)}
      closeLabel={t('common.close')}
    >
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          <Badge tone={statusTone[conn?.status ?? 'NOT_CONFIGURED']}>
            {t(`integrationStatus.${conn?.status ?? 'NOT_CONFIGURED'}`)}
          </Badge>
          {conn?.lastTestedAt && (
            <span className="text-sm text-ink-3">
              {t('admin.integrations.lastTested')}: {formatDate(conn.lastTestedAt)}
            </span>
          )}
          {item.docsUrl && (
            <a
              href={item.docsUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="text-sm text-pine underline-offset-2 hover:underline"
            >
              {t('admin.integrations.docs')}
            </a>
          )}
        </div>
        {item.environments.length > 1 && (
          <Field label={t('admin.integrations.environment')} help={<HelpTip k="integrations.environment" />}>
            {(p) => (
              <Select
                {...p}
                value={environment}
                onChange={(e) => setEnv(e.target.value as 'sandbox' | 'live')}
              >
                {item.environments.map((e) => (
                  <option key={e} value={e}>
                    {t(`admin.integrations.env_${e}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        {conn?.hasSecrets && <p className="text-sm text-ink-2">{t('admin.integrations.reenterSecrets')}</p>}
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate(undefined);
          }}
        >
          <fieldset disabled={!manage} className="grid gap-4 sm:grid-cols-2">
            {item.fields.map((f) =>
              f.type === 'boolean' ? (
                <label key={f.key} className="flex items-center gap-2 text-sm sm:col-span-2">
                  <input
                    type="checkbox"
                    className="accent-[var(--zt-pine)]"
                    checked={Boolean(values[f.key])}
                    onChange={(e) => setValues({ ...values, [f.key]: e.target.checked })}
                  />
                  {t(`integrationFields.${f.key}`)}
                </label>
              ) : (
                <Field
                  key={f.key}
                  label={t(`integrationFields.${f.key}`)}
                  required={f.required}
                  optionalLabel={t('common.optional')}
                  error={fieldError(save.error, f.key)}
                >
                  {(p) => (
                    <Input
                      {...p}
                      type={
                        f.type === 'password'
                          ? 'password'
                          : f.type === 'number'
                            ? 'number'
                            : f.type === 'email'
                              ? 'email'
                              : 'text'
                      }
                      autoComplete="off"
                      value={String(values[f.key] ?? '')}
                      onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                    />
                  )}
                </Field>
              ),
            )}
          </fieldset>
          {manage && (
            <div className="flex flex-wrap items-center gap-2">
              <Button type="submit" variant="primary" loading={save.isPending}>
                {t('admin.integrations.save')}
              </Button>
              <HelpTip k="integrations.save_credentials" />
              <Button
                onClick={() => test.mutate(undefined)}
                loading={test.isPending}
                disabled={!conn?.hasSecrets}
              >
                {t('admin.integrations.test')}
              </Button>
              <HelpTip k="integrations.test" />
              {conn && (
                <>
                  <ConfirmButton
                    message={t('admin.integrations.confirmDisconnect')}
                    onConfirm={() => disconnect.mutateAsync(undefined)}
                  >
                    {t('admin.integrations.disconnect')}
                  </ConfirmButton>
                  <HelpTip k="integrations.disconnect" />
                </>
              )}
            </div>
          )}
        </form>
        {conn?.lastTestResult && !conn.lastTestResult.ok && (
          <p
            className="rounded-sm border border-rose/30 bg-rose-tint px-3 py-2 text-sm text-rose"
            role="alert"
          >
            {testMessage(t, conn.lastTestResult)}
          </p>
        )}
        <div>
          <p className="mb-2 font-cond font-semibold">{t('admin.integrations.logs')}</p>
          {!logs.data?.length ? (
            <p className="text-sm text-ink-3">{t('admin.integrations.emptyLogs')}</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {logs.data.slice(0, 20).map((l) => (
                <li key={l.id} className="flex flex-wrap gap-x-3">
                  <span className="text-ink-3">{formatDate(l.createdAt)}</span>
                  <span>{t(`admin.integrations.log_${l.operation.replace('.', '_')}`)}</span>
                  <span
                    className={
                      l.outcome === 'success'
                        ? 'text-moss'
                        : l.outcome === 'blocked'
                          ? 'text-amber'
                          : 'text-rose'
                    }
                  >
                    {t(`admin.integrations.logOutcome_${l.outcome}`)}
                  </span>
                  {typeof (l.summary as { to?: unknown } | null)?.to === 'string' && (
                    <span className="text-ink-2">{(l.summary as { to: string }).to}</span>
                  )}
                  {l.durationMs !== null && <span className="text-ink-3">{l.durationMs} ms</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Dialog>
  );
}

export function IntegrationsPage() {
  const t = useT();
  const meta = useMeta();
  const implemented = meta.data?.implementedPhase ?? 1;
  const q = useQuery({
    queryKey: ['integrations'],
    queryFn: () => api.get<Integration[]>('/v1/admin/integrations'),
  });
  const [open, setOpen] = useState<Integration | null>(null);
  const groups = useMemo(() => {
    const m = new Map<string, Integration[]>();
    for (const i of q.data ?? []) m.set(i.category, [...(m.get(i.category) ?? []), i]);
    return [...m.entries()];
  }, [q.data]);
  return (
    <PageFrame helpKey="page.integrations">
      {q.data && <HealthSection items={q.data} />}
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : (
        groups.map(([cat, items]) => (
          <Section
            key={cat}
            title={t(`integrationCategories.${cat}`)}
            description={
              cat === 'payment' ? (
                <>
                  {t('admin.integrations.paymentNote')}{' '}
                  <Link to="/odeme-ayarlari" className="text-pine underline underline-offset-2">
                    {t('admin.nav.paymentSettings')}
                  </Link>
                </>
              ) : undefined
            }
          >
            {cat === 'email' && <EmailRoutingSection />}
            <ul className="divide-y divide-line rounded-md border border-line bg-surface">
              {items.map((i) => (
                <li key={i.key} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{t(`integrations.${i.key}.name`)}</p>
                    <p className="text-sm text-ink-2">{t(`integrations.${i.key}.description`)}</p>
                    <LastHealth item={i} />
                  </div>
                  <Badge tone={statusTone[i.status]}>{t(`integrationStatus.${i.status}`)}</Badge>
                  {i.managedAt ? (
                    <Link
                      to={i.managedAt}
                      className="text-sm font-medium text-pine underline underline-offset-2"
                    >
                      {i.managedAt === '/google'
                        ? t('admin.integrations.manageOnGoogle')
                        : t('admin.integrations.manageOnTracking')}
                    </Link>
                  ) : i.adapterAvailable ? (
                    <span className="flex items-center gap-3">
                      {i.key === 'bizimhesap' && (
                        <Link
                          to="/bizimhesap"
                          className="text-sm font-medium text-pine underline underline-offset-2"
                        >
                          {t('admin.integrations.bizimhesapSettings')}
                        </Link>
                      )}
                      <Button size="sm" onClick={() => setOpen(i)}>
                        {t('admin.integrations.configure')}
                      </Button>
                    </span>
                  ) : (
                    <span className="max-w-md text-sm text-ink-3">
                      {/* Kargo firmaları (F7-28): doğrudan bağlantı kurumsal sözleşme ister; Geliver üzerinden gönderilir. */}
                      {['yurtici', 'aras', 'mng'].includes(i.key)
                        ? t('admin.integrations.carrierViaGeliver')
                        : i.key === 'paynet'
                          ? t('admin.integrations.pendingMerged')
                          : ['rank_tracking', 'cloudflare'].includes(i.key)
                            ? t('admin.integrations.pendingDecision')
                            : i.phase <= implemented
                              ? t('admin.integrations.adapterPending')
                              : t('common.lockedPhase', { phase: i.phase })}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </Section>
        ))
      )}
      {open && (
        // Kayıt/test sonrası liste yenilenir; pencere güncel bağlantı durumunu göstersin.
        <IntegrationDialog
          key={open.key}
          item={q.data?.find((x) => x.key === open.key) ?? open}
          open
          onOpenChange={(o) => !o && setOpen(null)}
        />
      )}
    </PageFrame>
  );
}

const healthTone = { ok: 'moss', failed: 'rose', skipped: 'slate' } as const;

/**
 * Bağlantı denetimi (F12-02): kayıtlı bağlantıların yan etkisiz testi (mesaj göndermez, ücret doğurmaz). Bağlantının
 * durumunu değiştirmez; otomatik denetim 6 saatte bir çalışır, art arda iki hata Sistem Durumu uyarısı olur.
 */
function HealthSection({ items }: { items: Integration[] }) {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const [result, setResult] = useState<VerifyIntegrationsResult | null>(null);
  const verify = useApiMutation(
    () => api.post<VerifyIntegrationsResult>('/v1/admin/integrations/verify', {}),
    { invalidate: [['integrations']], onSuccess: (r) => setResult(r) },
  );
  const saved = items.flatMap((i) => i.connections).filter((c) => c.hasSecrets);
  if (!saved.length) return null;
  return (
    <Section
      title={t('admin.integrations.health.title')}
      description={t('admin.integrations.health.lead')}
      action={
        <span className="flex items-center gap-2">
          {can('org.integration.manage') && (
            <Button size="sm" loading={verify.isPending} onClick={() => verify.mutate(undefined)}>
              {t('admin.integrations.health.verify')}
            </Button>
          )}
          <HelpTip k="integrations.verify_all" />
        </span>
      }
    >
      {result && (
        <>
          <p role="status" className="mb-3 text-sm">
            {t('admin.integrations.health.summary', {
              ok: result.summary.ok,
              failed: result.summary.failed,
              skipped: result.summary.skipped,
              time: formatDate(result.checkedAt, true),
            })}
          </p>
          <div className="overflow-x-auto rounded-md border border-line">
            <table
              className="w-full min-w-[36rem] text-sm"
              aria-label={t('admin.integrations.health.tableCaption')}
            >
              <thead className="bg-surface-2 text-ink-2">
                <tr>
                  <th className="px-3 py-2 text-start font-medium">
                    {t('admin.integrations.health.colService')}
                  </th>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.integrations.environment')}</th>
                  <th className="px-3 py-2 text-start font-medium">
                    {t('admin.integrations.health.colResult')}
                  </th>
                  <th className="px-3 py-2 text-start font-medium">
                    {t('admin.integrations.health.colDetail')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {result.results.map((r) => (
                  <tr key={r.connectionId} className="border-t border-line align-top">
                    <td className="px-3 py-2 font-medium">{t(`integrations.${r.providerKey}.name`)}</td>
                    <td className="px-3 py-2">{t(`admin.integrations.env_${r.environment}`)}</td>
                    <td className="px-3 py-2">
                      <Badge tone={healthTone[r.outcome]}>
                        {t(`admin.integrations.health.outcome_${r.outcome}`)}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-ink-2">{testMessage(t, r)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Section>
  );
}

/** Listede bağlantının son otomatik/toplu denetimi (yalnızca denetlenmişse). */
function LastHealth({ item }: { item: Integration }) {
  const t = useT();
  const { formatDate } = useI18n();
  const last = item.connections
    .map((c) => c.lastHealth)
    .filter((h): h is NonNullable<typeof h> => h !== null)
    .sort((a, b) => (a.at < b.at ? 1 : -1))[0];
  if (!last) return null;
  return (
    <p className={`mt-1 text-xs ${last.ok ? 'text-ink-3' : 'text-rose'}`}>
      {t(last.ok ? 'admin.integrations.health.lastOk' : 'admin.integrations.health.lastFailed', {
        time: formatDate(last.at, true),
      })}
    </p>
  );
}

/**
 * E-posta gönderimi (F6-04): işlem ve pazarlama e-postalarının sağlayıcısı; geri dönen/şikâyet bildirim adresleri.
 */
function EmailRoutingSection() {
  const t = useT();
  const { can } = usePermissions();
  const manage = can('org.integration.manage');
  const q = useQuery({
    queryKey: ['email-routing'],
    queryFn: () => api.get<EmailRouting>('/v1/admin/email-routing'),
  });
  const [form, setForm] = useState<{ transactional: string; marketing: string } | null>(null);
  const d = q.data;
  const f = form ?? { transactional: d?.transactional ?? '', marketing: d?.marketing ?? '' };
  const save = useApiMutation(
    () =>
      api.put<EmailRouting>('/v1/admin/email-routing', {
        transactional: f.transactional || null,
        marketing: f.marketing || null,
      }),
    {
      invalidate: [['email-routing'], ['setup'], ['system-status']],
      success: t('admin.emailRouting.saved'),
      onSuccess: () => setForm(null),
    },
  );
  const rotate = useApiMutation(() => api.post<EmailRouting>('/v1/admin/email-routing/rotate-token', {}), {
    invalidate: [['email-routing']],
    success: t('admin.emailRouting.rotated'),
  });
  if (!d) return <Spinner label={t('common.loading')} />;
  const apiLive = d.live.filter((p) => p !== 'smtp');
  const pick = (k: 'transactional' | 'marketing') => (
    <Field
      label={t(`admin.emailRouting.${k}`)}
      help={<HelpTip k={`email_routing.${k}`} />}
      className="min-w-0 flex-1"
    >
      {(p) => (
        <Select
          {...p}
          value={f[k]}
          disabled={!manage}
          onChange={(e) => setForm({ ...f, [k]: e.target.value })}
        >
          <option value="">
            {k === 'marketing'
              ? t('admin.emailRouting.sameAsTransactional')
              : t('admin.emailRouting.smtpDefault')}
          </option>
          {apiLive.map((p) => (
            <option key={p} value={p}>
              {t(`integrations.${p}.name`)}
            </option>
          ))}
        </Select>
      )}
    </Field>
  );
  return (
    <div className="mb-4 space-y-4 rounded-md border border-line bg-surface p-4">
      <div>
        <p className="font-medium">{t('admin.emailRouting.title')}</p>
        <p className="text-sm text-ink-2">{t('admin.emailRouting.lead')}</p>
      </div>
      {apiLive.length === 0 && <p className="text-sm text-ink-3">{t('admin.emailRouting.noProviders')}</p>}
      <div className="flex flex-wrap items-end gap-3">
        {pick('transactional')}
        {pick('marketing')}
        {manage && (
          <span className="inline-flex items-center gap-1">
            <Button loading={save.isPending} disabled={!form} onClick={() => save.mutate(undefined)}>
              {t('admin.emailRouting.save')}
            </Button>
            <HelpTip k="email_routing.save" />
          </span>
        )}
      </div>
      {manage && d.webhooks && apiLive.length > 0 && (
        <div className="space-y-3 border-t border-line pt-4">
          <p className="flex items-center gap-1 text-sm text-ink-2">
            {t('admin.emailRouting.webhooksLead')} <HelpTip k="email_routing.webhooks" />
          </p>
          {apiLive.map((p) => (
            <SecretReveal
              key={p}
              label={t('admin.emailRouting.webhookFor', { provider: t(`integrations.${p}.name`) })}
              value={d.webhooks![p as keyof typeof d.webhooks]}
            />
          ))}
          <span className="inline-flex items-center gap-1">
            <ConfirmButton
              variant="secondary"
              message={t('admin.emailRouting.rotateConfirm')}
              onConfirm={() => rotate.mutateAsync(undefined)}
            >
              {t('admin.emailRouting.rotate')}
            </ConfirmButton>
            <HelpTip k="email_routing.rotate" />
          </span>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ API anahtarları

function CreateKeyDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const t = useT();
  const meta = useMeta();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>([]);
  const [expiresAt, setExpiresAt] = useState('');
  const [created, setCreated] = useState<CreatedApiKey | null>(null);
  const create = useApiMutation(
    () =>
      api.post<CreatedApiKey>('/v1/admin/api-keys', {
        name,
        scopes,
        expiresAt: expiresAt ? new Date(`${expiresAt}T23:59:59`).toISOString() : null,
      }),
    { invalidate: [['api-keys']], onSuccess: (r) => setCreated(r), silentFieldErrors: true },
  );
  const allowed = (meta.data?.permissions ?? []).filter((p) => p.apiKey);
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      variant="sheet"
      title={created ? t('admin.apiKeys.secretTitle') : t('admin.apiKeys.create')}
      closeLabel={t('common.close')}
      footer={
        created ? (
          <Button variant="primary" onClick={() => onOpenChange(false)}>
            {t('common.close')}
          </Button>
        ) : (
          <>
            <Button onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              loading={create.isPending}
              disabled={!name || scopes.length === 0}
              onClick={() => create.mutate(undefined)}
            >
              {t('common.create')}
            </Button>
          </>
        )
      }
    >
      {created ? (
        <div className="space-y-5">
          <p className="text-ink-2">{t('admin.apiKeys.secretLead')}</p>
          <SecretReveal value={created.secret} label={created.name} />
          <div>
            <p className="mb-1 text-sm font-medium">{t('admin.apiKeys.usage')}</p>
            <pre className="overflow-x-auto rounded-sm bg-surface-2 p-3 text-[0.8125rem]">{`curl -H "Authorization: Bearer ${created.prefix}_…" \\\n  ${location.origin}/v1/admin/brands`}</pre>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <Field label={t('admin.apiKeys.name')} required error={fieldError(create.error, 'name')}>
            {(p) => (
              <Input
                {...p}
                placeholder={t('admin.apiKeys.namePlaceholder')}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            )}
          </Field>
          <Field label={t('admin.apiKeys.expiresAt')} optionalLabel={t('common.optional')}>
            {(p) => (
              <Input {...p} type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
            )}
          </Field>
          <div>
            <div className="mb-2 flex items-center gap-1">
              <p className="text-sm font-medium">{t('admin.apiKeys.scopes')}</p>
              <HelpTip k="api_keys.scopes" />
            </div>
            <ul className="grid gap-1.5 sm:grid-cols-2">
              {allowed.map((p) => (
                <li key={p.key}>
                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="mt-1 accent-[var(--zt-pine)]"
                      checked={scopes.includes(p.key)}
                      onChange={(e) =>
                        setScopes(e.target.checked ? [...scopes, p.key] : scopes.filter((s) => s !== p.key))
                      }
                    />
                    <span className="text-ink-2">{t(`permissions.${p.key}`)}</span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </Dialog>
  );
}

export function ApiKeysPage() {
  const t = useT();
  const { formatDate, formatRelative } = useI18n();
  const q = useQuery({ queryKey: ['api-keys'], queryFn: () => api.get<ApiKey[]>('/v1/admin/api-keys') });
  const revoke = useApiMutation((id: string) => api.post(`/v1/admin/api-keys/${id}/revoke`), {
    invalidate: [['api-keys']],
    success: t('common.saved'),
  });
  const [open, setOpen] = useState(false);
  return (
    <PageFrame
      helpKey="page.api_keys"
      actions={
        <div className="flex items-center gap-1">
          <a
            href="/v1/docs"
            target="_blank"
            rel="noreferrer"
            className="me-2 text-sm text-pine underline-offset-2 hover:underline"
          >
            {t('admin.apiKeys.docs')}
          </a>
          <Button variant="primary" onClick={() => setOpen(true)}>
            {t('admin.apiKeys.create')}
          </Button>
          <HelpTip k="api_keys.create" />
        </div>
      }
    >
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : q.data.length === 0 ? (
        <EmptyState title={t('admin.apiKeys.emptyTitle')} description={t('admin.apiKeys.emptyLead')} />
      ) : (
        <ul className="divide-y divide-line rounded-md border border-line bg-surface">
          {q.data.map((k) => {
            const expired = k.expiresAt && new Date(k.expiresAt) < new Date();
            return (
              <li key={k.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{k.name}</p>
                  <p className="tabular text-sm text-ink-3">{k.prefix}_…</p>
                </div>
                <span className="text-sm text-ink-2">
                  {t('admin.roles.permissionCount', { count: k.scopes.length })}
                </span>
                <span className="text-sm text-ink-3">
                  {t('common.lastUsed')}: {formatRelative(k.lastUsedAt)}
                </span>
                <span className="text-sm text-ink-3">
                  {k.expiresAt ? formatDate(k.expiresAt, false) : t('admin.apiKeys.noExpiry')}
                </span>
                {k.revokedAt ? (
                  <Badge tone="slate">{t('admin.apiKeys.revoked')}</Badge>
                ) : expired ? (
                  <Badge tone="amber">{t('admin.apiKeys.expired')}</Badge>
                ) : (
                  <div className="flex items-center gap-1">
                    <ConfirmButton
                      message={t('admin.apiKeys.confirmRevoke')}
                      onConfirm={() => revoke.mutateAsync(k.id)}
                    >
                      {t('admin.apiKeys.revoke')}
                    </ConfirmButton>
                    <HelpTip k="api_keys.revoke" />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {open && <CreateKeyDialog open={open} onOpenChange={setOpen} />}
    </PageFrame>
  );
}

// ------------------------------------------------------------------ Webhook

function WebhookDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const t = useT();
  const meta = useMeta();
  const [url, setUrl] = useState('https://');
  const [description, setDescription] = useState('');
  const [events, setEvents] = useState<string[]>(['*']);
  const [created, setCreated] = useState<CreatedWebhook | null>(null);
  const create = useApiMutation(
    () =>
      api.post<CreatedWebhook>('/v1/admin/webhooks', {
        url,
        description: description || null,
        eventTypes: events,
      }),
    {
      invalidate: [['webhooks']],
      onSuccess: (r) => setCreated(r),
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      variant="sheet"
      title={created ? t('admin.webhooks.secretTitle') : t('admin.webhooks.add')}
      closeLabel={t('common.close')}
      footer={
        created ? (
          <Button variant="primary" onClick={() => onOpenChange(false)}>
            {t('common.close')}
          </Button>
        ) : (
          <>
            <Button onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              loading={create.isPending}
              disabled={events.length === 0}
              onClick={() => create.mutate(undefined)}
            >
              {t('common.create')}
            </Button>
          </>
        )
      }
    >
      {created ? (
        <div className="space-y-4">
          <p className="text-ink-2">{t('admin.webhooks.secretLead')}</p>
          <SecretReveal value={created.signingSecret} label={created.url} />
          <p className="text-sm text-ink-3">{t('admin.webhooks.signatureHelp')}</p>
        </div>
      ) : (
        <div className="space-y-5">
          <Field label={t('admin.webhooks.url')} required error={fieldError(create.error, 'url')}>
            {(p) => <Input {...p} type="url" value={url} onChange={(e) => setUrl(e.target.value)} />}
          </Field>
          <Field label={t('admin.webhooks.description')} optionalLabel={t('common.optional')}>
            {(p) => <Input {...p} value={description} onChange={(e) => setDescription(e.target.value)} />}
          </Field>
          <div>
            <div className="mb-2 flex items-center gap-1">
              <p className="text-sm font-medium">{t('admin.webhooks.events')}</p>
              <HelpTip k="webhooks.events" />
            </div>
            <label className="mb-2 flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                className="accent-[var(--zt-pine)]"
                checked={events.includes('*')}
                onChange={(e) => setEvents(e.target.checked ? ['*'] : [])}
              />
              {t('admin.webhooks.allEvents')}
            </label>
            {!events.includes('*') && (
              <ul className="grid gap-1.5 sm:grid-cols-2">
                {meta.data?.eventTypes.map((ev) => (
                  <li key={ev}>
                    <label className="flex items-start gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="mt-1 accent-[var(--zt-pine)]"
                        checked={events.includes(ev)}
                        onChange={(e) =>
                          setEvents(e.target.checked ? [...events, ev] : events.filter((x) => x !== ev))
                        }
                      />
                      <span>
                        <span className="text-ink-2">{t(`events.${ev}`)}</span>{' '}
                        <code className="text-[0.75rem] text-ink-3">{ev}</code>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </Dialog>
  );
}

function Deliveries({ webhookId }: { webhookId: string }) {
  const t = useT();
  const { formatDate } = useI18n();
  const q = useQuery({
    queryKey: ['deliveries', webhookId],
    queryFn: () => api.get<Delivery[]>(`/v1/admin/webhooks/${webhookId}/deliveries`),
    refetchInterval: 5000,
  });
  const redeliver = useApiMutation(
    (id: string) => api.post(`/v1/admin/webhooks/deliveries/${id}/redeliver`),
    { invalidate: [['deliveries', webhookId]] },
  );
  const tone: Record<string, Tone> = { pending: 'amber', succeeded: 'moss', failed: 'rose', dead: 'rose' };
  if (!q.data) return <Spinner label={t('common.loading')} />;
  if (q.data.length === 0) return <p className="text-sm text-ink-3">{t('admin.webhooks.emptyDeliveries')}</p>;
  return (
    <ul className="divide-y divide-line text-sm">
      {q.data.map((d) => (
        <li key={d.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
          <span className="text-ink-3">{formatDate(d.createdAt)}</span>
          <code className="text-ink-2">{d.eventType}</code>
          <Badge tone={tone[d.status]}>{t(`admin.webhooks.delivery_${d.status}`)}</Badge>
          <span className="text-ink-3">
            {t('admin.webhooks.attempt')}: {d.attempt}
          </span>
          {d.responseStatus !== null && (
            <span className="text-ink-3">
              {t('admin.webhooks.response')}: {d.responseStatus}
            </span>
          )}
          {d.status !== 'succeeded' && (
            <Button size="sm" variant="ghost" onClick={() => redeliver.mutate(d.id)}>
              {t('admin.webhooks.redeliver')}
            </Button>
          )}
        </li>
      ))}
    </ul>
  );
}

export function WebhooksPage() {
  const t = useT();
  const q = useQuery({ queryKey: ['webhooks'], queryFn: () => api.get<Webhook[]>('/v1/admin/webhooks') });
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [rotated, setRotated] = useState<{ id: string; signingSecret: string } | null>(null);
  const inv = [['webhooks']];
  const testEvent = useApiMutation(
    () => api.post('/v1/admin/webhooks/test', undefined, { idempotencyKey: newIdempotencyKey() }),
    { success: t('admin.webhooks.testSent') },
  );
  const toggle = useApiMutation(
    (w: Webhook) =>
      api.patch(`/v1/admin/webhooks/${w.id}`, { status: w.status === 'active' ? 'disabled' : 'active' }),
    { invalidate: inv },
  );
  const del = useApiMutation((id: string) => api.del(`/v1/admin/webhooks/${id}`), { invalidate: inv });
  const rotate = useApiMutation(
    (id: string) => api.post<{ id: string; signingSecret: string }>(`/v1/admin/webhooks/${id}/rotate-secret`),
    { onSuccess: (r) => setRotated(r) },
  );
  return (
    <PageFrame
      helpKey="page.webhooks"
      actions={
        <div className="flex items-center gap-1">
          <Button
            onClick={() => testEvent.mutate(undefined)}
            loading={testEvent.isPending}
            disabled={!q.data?.length}
          >
            {t('admin.webhooks.test')}
          </Button>
          <HelpTip k="webhooks.test" />
          <Button variant="primary" onClick={() => setOpen(true)}>
            {t('admin.webhooks.add')}
          </Button>
          <HelpTip k="webhooks.create" />
        </div>
      }
    >
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : q.data.length === 0 ? (
        <EmptyState title={t('admin.webhooks.emptyTitle')} description={t('admin.webhooks.emptyLead')} />
      ) : (
        <ul className="divide-y divide-line rounded-md border border-line bg-surface">
          {q.data.map((w) => (
            <li key={w.id} className="px-4 py-3">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <div className="min-w-0 flex-1">
                  <p className="font-medium break-all">{w.url}</p>
                  <p className="text-sm text-ink-3">
                    {w.eventTypes.includes('*') ? t('admin.webhooks.allEvents') : w.eventTypes.join(', ')}
                  </p>
                </div>
                <Badge tone={w.status === 'active' ? 'moss' : 'slate'}>
                  {w.status === 'active' ? t('admin.webhooks.active') : t('admin.webhooks.disabled')}
                </Badge>
                <div className="flex flex-wrap items-center gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-expanded={expanded === w.id}
                    onClick={() => setExpanded(expanded === w.id ? null : w.id)}
                  >
                    {t('admin.webhooks.deliveries')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => toggle.mutate(w)}>
                    {w.status === 'active' ? t('admin.webhooks.disable') : t('admin.webhooks.enable')}
                  </Button>
                  <ConfirmButton
                    variant="secondary"
                    message={t('admin.webhooks.confirmRotate')}
                    onConfirm={() => rotate.mutateAsync(w.id)}
                  >
                    {t('admin.webhooks.rotate')}
                  </ConfirmButton>
                  <HelpTip k="webhooks.rotate_secret" />
                  <ConfirmButton
                    message={t('admin.webhooks.confirmDelete')}
                    onConfirm={() => del.mutateAsync(w.id)}
                  >
                    {t('admin.webhooks.delete')}
                  </ConfirmButton>
                  <HelpTip k="webhooks.delete" />
                </div>
              </div>
              {expanded === w.id && (
                <div className="mt-3 border-t border-line pt-3">
                  <Deliveries webhookId={w.id} />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {open && <WebhookDialog open={open} onOpenChange={setOpen} />}
      {rotated && (
        <Dialog
          open
          onOpenChange={() => setRotated(null)}
          title={t('admin.webhooks.secretTitle')}
          closeLabel={t('common.close')}
        >
          <div className="space-y-3">
            <p className="text-ink-2">{t('admin.webhooks.secretLead')}</p>
            <SecretReveal value={rotated.signingSecret} label={t('admin.webhooks.secretTitle')} />
          </div>
        </Dialog>
      )}
    </PageFrame>
  );
}
